import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Linkedin, 
  Twitter, 
  Facebook, 
  ArrowUp,
  Zap,
  Globe,
  Shield,
  ChevronRight
} from 'lucide-react';
import { cn } from '../../utils/cn';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { key: 'home', path: '/', icon: Globe },
    { key: 'about', path: '/about', icon: Shield },
    // { key: 'services', path: '/services', icon: Zap },
    { key: 'clients', path: '/clients-industries', icon: Globe },
    { key: 'contact', path: '/contact', icon: Mail },
  ];

  const divisions = [
    { key: 'telecommunication', path: '/divisions/telecommunication' },
    { key: 'power', path: '/divisions/power' },
    { key: 'hazardous', path: '/divisions/hazardous' },
    { key: 'commercial', path: '/divisions/commercial' },
  ];

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn', color: 'hover:text-global-light-blue' },
    { icon: Twitter, href: '#', label: 'Twitter', color: 'hover:text-global-light-blue' },
    { icon: Facebook, href: '#', label: 'Facebook', color: 'hover:text-global-dark-blue' },
  ];

  const contactInfo = [
    { icon: Mail, text: 'info@globaltechnology.com', href: 'mailto:info@globaltechnology.com' },
    { icon: Phone, text: '+971 4 XXX XXXX', href: 'tel:+97144XXXXXX' },
    { icon: MapPin, text: 'Dubai, UAE', href: '#' },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const glowVariants = {
    initial: { scale: 1, opacity: 0.5 },
    animate: {
      scale: [1, 1.2, 1],
      opacity: [0.5, 0.8, 0.5],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <footer className="relative bg-black text-white overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Background Image on Right Side */}
        <motion.div
          className="absolute top-0 right-0 w-[50%] h-[100%] bg-cover bg-center"
          style={{ backgroundImage: 'url(/map.png)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1 }}
        />

        
      </div>

      <div className="container-max relative z-10">
        <div className="py-8 px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          >
            {/* Company Info */}
            <motion.div
              variants={itemVariants}
              className="lg:col-span-2 space-y-4"
            >
              {/* Logo Section */}
              <motion.div 
                className="flex items-center space-x-3 group"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <div className="relative">
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-global-green to-global-light-blue rounded-xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                    animate={{
                      rotate: [0, 360],
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  />
                  <img 
                    src="/logo.svg" 
                    alt="Global Technology" 
                    className="relative h-12 w-12 drop-shadow-lg"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-white to-slate bg-clip-text text-transparent">
                    Global Technology
                  </h2>
                  <p className="text-sm text-global-light-blue font-medium">
                    Engineering Excellence
                  </p>
                </div>
              </motion.div>
              
              {/* Description */}
              <motion.p 
                className="text-slate/90 leading-relaxed max-w-md"
                whileHover={{ color: "rgba(255,255,255,0.9)" }}
                transition={{ duration: 0.3 }}
              >
                {t('footer.description')}
              </motion.p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                {contactInfo.map((contact, index) => {
                  const Icon = contact.icon;
                  return (
                    <motion.a
                      key={index}
                      href={contact.href}
                      className="flex items-center space-x-3 group cursor-pointer"
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <motion.div
                        className="w-8 h-8 bg-gradient-to-br from-global-green/20 to-global-light-blue/20 rounded-lg flex items-center justify-center group-hover:from-global-green/40 group-hover:to-global-light-blue/40 transition-all duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Icon className="w-4 h-4 text-global-light-blue group-hover:text-white transition-colors duration-300" />
                      </motion.div>
                      <span className="text-global-light-blue group-hover:text-white transition-colors duration-300 text-sm">
                        {contact.text}
                      </span>
                    </motion.a>
                  );
                })}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div variants={itemVariants}>
              <motion.h3 
                className="text-lg font-bold mb-4 bg-gradient-to-r from-white to-global-light-blue bg-clip-text text-transparent"
                whileHover={{ scale: 1.05 }}
              >
                {t('footer.quickLinks')}
              </motion.h3>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <motion.li 
                      key={link.key}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Link
                        to={link.path}
                        className="flex items-center space-x-3 text-global-light-blue hover:text-white group transition-all duration-300"
                      >
                        <motion.div
                          className="w-6 h-6 bg-gradient-to-br from-slate/20 to-transparent rounded-md flex items-center justify-center group-hover:from-global-light-blue/30 group-hover:to-global-green/30 transition-all duration-300"
                          whileHover={{ scale: 1.1 }}
                        >
                          <Icon className="w-3 h-3 group-hover:text-global-light-blue transition-colors duration-300" />
                        </motion.div>
                        <span className="group-hover:translate-x-1 transition-transform duration-300 text-sm">
                          {t(`nav.${link.key}`)}
                        </span>
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                      </Link>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>

            {/* Divisions */}
            <motion.div variants={itemVariants}>
              <motion.h3 
                className="text-lg font-bold mb-4 bg-gradient-to-r from-white to-global-green bg-clip-text text-transparent"
                whileHover={{ scale: 1.05 }}
              >
                {t('footer.divisions')}
              </motion.h3>
              <ul className="space-y-2">
                {divisions.map((division, index) => (
                  <motion.li 
                    key={division.key}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={division.path}
                      className="flex items-center justify-between text-global-light-blue hover:text-white group transition-all duration-300 py-1 px-2 rounded-lg hover:bg-gradient-to-r hover:from-slate/10 hover:to-transparent"
                    >
                      <span className="group-hover:translate-x-1 transition-transform duration-300 text-sm">
                        {t(`nav.${division.key}`)}
                      </span>
                      <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:text-global-light-blue transition-all duration-300" />
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-8 pt-6 border-t border-gradient-to-r from-transparent via-slate/30 to-transparent"
          >
            {/* Social Links & Newsletter */}
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0 mb-6">
              {/* Social Links */}
              <div className="flex items-center space-x-2">
                <motion.span 
                  className="text-slate mr-3 font-medium text-sm"
                  whileHover={{ color: "rgba(255,255,255,0.9)" }}
                >
                  {t('footer.followUs')}:
                </motion.span>
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      className={cn(
                        "w-10 h-10 bg-gradient-to-br from-slate/20 to-transparent rounded-xl flex items-center justify-center text-slate transition-all duration-300 hover:scale-110",
                        social.color
                      )}
                      whileHover={{ 
                        scale: 1.15,
                        rotate: 5,
                        boxShadow: "0 10px 25px rgba(0,169,228,0.3)"
                      }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      aria-label={social.label}
                    >
                      <Icon className="w-4 h-4" />
                    </motion.a>
                  );
                })}
              </div>

              {/* Newsletter Signup */}
              <motion.div 
                className="flex items-center space-x-3"
                whileHover={{ scale: 1.02 }}
              >
                <span className="text-slate font-medium hidden sm:block text-sm">Stay Updated:</span>
                <div className="flex">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="px-3 py-2 bg-slate/10 border border-slate/30 rounded-l-lg text-white placeholder-slate/60 focus:outline-none focus:border-global-light-blue transition-colors duration-300 text-sm"
                  />
                  <motion.button
                    className="px-4 py-2 bg-gradient-to-r from-global-green to-global-light-blue rounded-r-lg text-white font-medium hover:from-global-light-blue hover:to-global-green transition-all duration-300 text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Subscribe
                  </motion.button>
                </div>
              </motion.div>
            </div>

            {/* Copyright & Legal */}
            <motion.div
              className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0 pt-4 border-t border-slate/20"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-wrap items-center justify-center md:justify-start space-x-4 text-xs">
                <Link
                  to="/privacy"
                  className="text-slate hover:text-global-light-blue transition-colors duration-300 hover:underline"
                >
                  {t('footer.privacy')}
                </Link>
                <Link
                  to="/terms"
                  className="text-slate hover:text-global-light-blue transition-colors duration-300 hover:underline"
                >
                  {t('footer.terms')}
                </Link>
                <span className="text-slate">|</span>
                <p className="text-slate">
                  {t('footer.copyright')}
                </p>
              </div>
              
              <motion.div
                className="text-xs text-slate"
                whileHover={{ color: "rgba(255,255,255,0.9)" }}
              >
                {/* Made by <a href="https://aiorbitlabs.com" className="text-global-light-blue hover:underline">aiorbitlabs.com</a> */}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-global-green to-global-light-blue rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl z-50"
            whileHover={{ 
              scale: 1.1,
              boxShadow: "0 20px 40px rgba(0,169,228,0.4)"
            }}
            whileTap={{ scale: 0.9 }}
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </footer>
  );
};

export default Footer;